import React from "react";
import { useAuth } from "../auth/useAuth";
import { useJobTracker, type JobInfo } from "../jobs/JobTrackerContext";
import { TENANT_BACKUP_RESTORE_JOB_TYPE, isImportAdapterCommitJob } from "../../pages/admin/importAdapters/importAdapterJobs";
import { useRealtimeEvent } from "../realtime/RealtimeContext";
import { RealtimeEventTypes } from "../realtime/realtimeEventTypes";
import { requestAppReload } from "./appReloadCoordinator";

const JOB_STATUS_SUCCEEDED = "succeeded";

const TENANT_DATA_RELOAD_DELAY_MS = 150;

/** Jobs, nach deren Abschluss die Mandantendaten ersetzt sind: jeder Adapter-Import und die Wiederherstellung. */
function isTenantDataReplacingJob(job: JobInfo) {
  return isImportAdapterCommitJob(job) || job.jobType === TENANT_BACKUP_RESTORE_JOB_TYPE;
}

function isSucceeded(status: string | null | undefined): boolean {
  return String(status ?? "").trim().toLowerCase() === JOB_STATUS_SUCCEEDED;
}

export function AppReloadEvents() {
  const auth = useAuth();
  const { jobs } = useJobTracker();
  const tenantId = (auth.info?.tenantId ?? "").trim();

  useRealtimeEvent<{ operation?: string; snapshotAt?: string }>(
    RealtimeEventTypes.TenantDataChanged,
    (message) => {
      const eventTenantId = (message.tenantId ?? "").trim();
      if (!tenantId || !eventTenantId || eventTenantId.toLowerCase() !== tenantId.toLowerCase()) return;
      const marker = [
        "event",
        eventTenantId,
        message.entityId ?? "",
        message.action ?? "",
        message.createdAt ?? message.payload?.snapshotAt ?? ""
      ].join(":");
      requestAppReload({
        reason: "tenant-data-replaced",
        dedupeKey: marker,
        delayMs: TENANT_DATA_RELOAD_DELAY_MS
      });
    },
    { enabled: !!tenantId }
  );

  React.useEffect(() => {
    if (!tenantId) return;
    for (const job of Object.values(jobs)) {
      if (!isTenantDataReplacingJob(job)) continue;
      if (!isSucceeded(job.status)) continue;
      const jobTenantId = (job.tenantId ?? "").trim();
      if (!jobTenantId || jobTenantId.toLowerCase() !== tenantId.toLowerCase()) continue;
      requestAppReload({
        reason: "tenant-data-replaced",
        dedupeKey: ["job", jobTenantId, job.id, job.finishedAt ?? ""].join(":"),
        delayMs: TENANT_DATA_RELOAD_DELAY_MS
      });
      return;
    }
  }, [jobs, tenantId]);

  return null;
}
